import { useRef, useState } from 'react';

import { Separator } from '@nesso/ui/primitives/separator';
import { Toggle } from '@nesso/ui/primitives/toggle';

import { type RiskInput, riskSchema } from '@/entities/session-note';
import { CheckboxList } from '@/shared/ui/checkbox-list/checkbox-list';
import { NoteTextarea } from '@/shared/ui/common/note-textarea';
import { FieldSection } from '@/shared/ui/forms/field-section';

type RiskAnswer = 'yes' | 'no';
type RiskContent = RiskInput;

const RISK_AREA_OPTIONS = riskSchema.shape.areaOfRisk
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const RISK_DISPOSITION_OPTIONS = riskSchema.shape.disposition
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const RISK_PROTECTIVE_FACTOR_OPTIONS = riskSchema.shape.protectiveFactors
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const RISK_FACTOR_OPTIONS = riskSchema.shape.riskFactors
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

type Props = {
    content?: RiskContent;
    onChange: (content: RiskContent | undefined) => void;
};

function hasAnyRiskContent(content: RiskContent | undefined): boolean {
    if (!content) return false;
    return (
        (content.areaOfRisk?.length ?? 0) > 0 ||
        (content.disposition?.length ?? 0) > 0 ||
        (content.protectiveFactors?.length ?? 0) > 0 ||
        (content.riskFactors?.length ?? 0) > 0 ||
        !!content.descriptionOfRisk ||
        !!content.safetyPlan
    );
}

export function NessoRiskSection({ content, onChange }: Props) {
    const [answer, setAnswer] = useState<RiskAnswer>(() =>
        hasAnyRiskContent(content) ? 'yes' : 'no',
    );
    const isYes = answer === 'yes';
    const stashedContentRef = useRef<RiskContent | null>(null);

    const handleAnswerChange = (next: RiskAnswer) => {
        setAnswer(next);
        if (next === 'no') {
            if (hasAnyRiskContent(content)) {
                stashedContentRef.current = content ?? null;
                onChange(undefined);
            }
        } else if (stashedContentRef.current) {
            onChange(stashedContentRef.current);
            stashedContentRef.current = null;
        }
    };

    const current = content ?? {};

    return (
        <FieldSection title="Risk" className="gap-10">
            <FieldSection label="Was any risk reported or observed?" className="gap-2">
                <div className="flex flex-col gap-6">
                    <div className="flex items-center gap-1">
                        <Toggle
                            type="button"
                            variant="outline"
                            size="lg"
                            pressed={answer === 'yes'}
                            onPressedChange={() => handleAnswerChange('yes')}
                        >
                            Yes
                        </Toggle>
                        <Toggle
                            type="button"
                            variant="outline"
                            size="lg"
                            pressed={answer === 'no'}
                            onPressedChange={() => handleAnswerChange('no')}
                        >
                            No
                        </Toggle>
                    </div>

                    {isYes && (
                        <div className="flex gap-4">
                            <Separator
                                orientation="vertical"
                                className="data-[orientation=vertical]:h-auto ml-6"
                            />
                            <div className="flex flex-1 flex-col gap-8 w-full pb-1">
                                <FieldSection label="Areas of risk" className="gap-4 mb-2">
                                    <CheckboxList
                                        options={RISK_AREA_OPTIONS}
                                        values={current.areaOfRisk ?? []}
                                        onValuesChange={(areaOfRisk) =>
                                            onChange({
                                                ...current,
                                                areaOfRisk: areaOfRisk as RiskContent['areaOfRisk'],
                                            })
                                        }
                                        columns={1}
                                    />
                                </FieldSection>

                                <FieldSection label="Disposition" className="gap-4 mb-2">
                                    <CheckboxList
                                        options={RISK_DISPOSITION_OPTIONS}
                                        values={current.disposition ?? []}
                                        onValuesChange={(disposition) =>
                                            onChange({
                                                ...current,
                                                disposition:
                                                    disposition as RiskContent['disposition'],
                                            })
                                        }
                                        columns={1}
                                    />
                                </FieldSection>

                                <FieldSection label="Protective factors" className="gap-4 mb-2">
                                    <CheckboxList
                                        options={RISK_PROTECTIVE_FACTOR_OPTIONS}
                                        values={current.protectiveFactors ?? []}
                                        onValuesChange={(protectiveFactors) =>
                                            onChange({
                                                ...current,
                                                protectiveFactors:
                                                    protectiveFactors as RiskContent['protectiveFactors'],
                                            })
                                        }
                                        columns={1}
                                    />
                                </FieldSection>

                                <FieldSection label="Risk factors" className="gap-4">
                                    <CheckboxList
                                        options={RISK_FACTOR_OPTIONS}
                                        values={current.riskFactors ?? []}
                                        onValuesChange={(riskFactors) =>
                                            onChange({
                                                ...current,
                                                riskFactors:
                                                    riskFactors as RiskContent['riskFactors'],
                                            })
                                        }
                                        columns={1}
                                    />
                                </FieldSection>

                                <FieldSection label="Description of risk" className="gap-2">
                                    <NoteTextarea
                                        value={current.descriptionOfRisk}
                                        onChange={(descriptionOfRisk) =>
                                            onChange({ ...current, descriptionOfRisk })
                                        }
                                        rows={4}
                                    />
                                </FieldSection>

                                <FieldSection label="Safety plan" className="gap-2">
                                    <NoteTextarea
                                        value={current.safetyPlan}
                                        onChange={(safetyPlan) =>
                                            onChange({ ...current, safetyPlan })
                                        }
                                        rows={4}
                                    />
                                </FieldSection>
                            </div>
                        </div>
                    )}
                </div>
            </FieldSection>
        </FieldSection>
    );
}
